import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { LanguageService } from 'src/app/language.service';
import {
  AUDIO_51,
  AUDIO_DOLBY_ATMOS,
  AUDIO_DOLBY_AUDIO,
  dolbyAtmosQualities,
  dolbyAudioQualities,
} from '../../common/const/common.const';
import { Tags } from '../../common/interfaces/display-info';
import { EntitlementStatus } from '../../enums/subscription';
import { AuthService } from '../../services/auth/auth.service';
import { ConfigService } from '../../services/config.service';
import { AccountProfileService } from '../../services/evergent/account/account-profile.service';
import { UserEntitlementService } from '../../services/evergent/entitlement/user-entitlement.service';
import { AppConfigKeys } from '../../utils/constants/app-config.constant';

// Use existing tizen declaration from browserUtils
declare const tizen: any;
declare const webOS: any;
declare const webOSDev: any;
declare const webapis: any;

// Extend Window interface for device detection properties
declare global {
  interface Window {
    support4k: boolean;
    dolbyAtmos: boolean;
    deviceModel: string;
    duid: any;
    xSupportedResolution: any;
    xSupportedAudio: any;
  }
}

export interface ContentDetailInfo {
  advisories: string;
  audioQualityTag: Tags;
  contentTags: string;
  customTags: Tags[] | null;
  heroTags: string;
  is4k: boolean;
  isAtmos: boolean;
  isDolbyAudio: boolean;
  rating: string;
  multiLingualAudio: string | null;
  is51: boolean;
}

export interface DeviceCapabilities {
  deviceModel: string;
  support4k: boolean;
  xSupportedResolution: string;
  xSupportedAudio: string[];
  dolbyAtmos: boolean;
  duid: string;
  userAgent: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContentDetailService {
  private deviceCapabilities: DeviceCapabilities = {
    deviceModel: '',
    support4k: false,
    xSupportedResolution: '',
    xSupportedAudio: [],
    dolbyAtmos: false,
    duid: '',
    userAgent: '',
  };

  constructor(
    private readonly userEntlmnt: UserEntitlementService,
    private readonly configService: ConfigService,
    private readonly authService: AuthService,
    private readonly accountProfileService: AccountProfileService,
    private readonly languageService: LanguageService
  ) {
    // Initialize device detection
    this.initDeviceCapabilities();
  }

  /**
   * Initialize device capabilities detection
   */
  private initDeviceCapabilities(): void {
    const ua = navigator.userAgent.toLowerCase();

    // Initialize window properties
    window.xSupportedResolution = '';
    window.xSupportedAudio = [];
    window.support4k = false;
    window.dolbyAtmos = false;

    if (ua.includes('tizen')) {
      this.detectTizenCapabilities();
    } else if (ua.includes('web0s')) {
      this.detectWebOSCapabilities();
    } else {
      this.detectFallbackCapabilities();
    }

    // Update service properties
    this.updateDeviceCapabilities();
  }

  /**
   * Detect Tizen device capabilities
   */
  private detectTizenCapabilities(): void {
    try {
      window.deviceModel = webapis.productinfo.getModel();
      window.duid = webapis.productinfo.getDuid();
      window.support4k = webapis.productinfo.isUdPanelSupported();
      window.xSupportedResolution = window.support4k ? '4K' : '';

      if (webapis.productinfo.is8KPanelSupported?.()) {
        window.xSupportedResolution = '8K';
      }

      const audioMode = window.tizen?.tvaudiocontrol.getOutputMode();
      if (audioMode?.includes('DOLBY')) {
        window.dolbyAtmos = true;
        window.xSupportedAudio.push('dolbyAtmos');
      }
    } catch (error) {
      this.detectFallbackCapabilities();
    }
  }

  /**
   * Detect webOS device capabilities
   */
  private detectWebOSCapabilities(): void {
    try {
      if (typeof webOS !== 'undefined' && webOS.deviceInfo) {
        webOS.deviceInfo((device: any) => {
          window.deviceModel = device.modelName;

          if (device.dolbyAtmos) {
            window.dolbyAtmos = true;
            window.xSupportedAudio.push('dolbyAtmos');
          }

          if (device.uhd8K) {
            window.support4k = true;
            window.xSupportedResolution = '8K';
          } else if (
            device.uhd ||
            (device.screenWidth === 4096 && device.screenHeight === 2160)
          ) {
            window.support4k = true;
            window.xSupportedResolution = '4K';
          } else if (device.screenWidth > 1920 && device.screenHeight > 1080) {
            window.xSupportedResolution = '2K';
          } else if (
            device.screenWidth === 1920 &&
            device.screenHeight === 1080
          ) {
            window.xSupportedResolution = 'FHD';
          } else if (
            device.screenWidth === 1280 &&
            device.screenHeight === 720
          ) {
            window.xSupportedResolution = 'HD';
          }

          if (webOSDev?.LGUDID) {
            webOSDev.LGUDID({
              onSuccess: (res: any) => {
                window.duid = res.id;
                this.updateDeviceCapabilities();
              },
              onFailure: (error: any) => {
                this.updateDeviceCapabilities();
              },
            });
          } else {
            this.updateDeviceCapabilities();
          }
        });
      } else {
        this.detectFallbackCapabilities();
      }
    } catch (error) {
      this.detectFallbackCapabilities();
    }
  }

  /**
   * Detect fallback capabilities based on screen resolution
   */
  private detectFallbackCapabilities(): void {
    const screenWidth = screen?.width || window.screen?.width || 1920;
    const screenHeight = screen?.height || window.screen?.height || 1080;

    if (screenWidth >= 4096 || screenHeight >= 2160) {
      window.support4k = true;
      window.xSupportedResolution = '4K';
    } else if (screenWidth >= 1920 || screenHeight >= 1080) {
      window.support4k = false;
      window.xSupportedResolution = 'FHD';
    } else {
      window.support4k = false;
      window.xSupportedResolution = 'HD';
    }

    // Try to extract device model from user agent
    const uaMatch = navigator.userAgent.match(/\(([^)]+)\)/);
    if (uaMatch && uaMatch[1]) {
      window.deviceModel = uaMatch[1].split(';')[0];
    }

    this.updateDeviceCapabilities();
  }

  /**
   * Update service device capabilities from window properties
   */
  private updateDeviceCapabilities(): void {
    this.deviceCapabilities = {
      deviceModel: window.deviceModel || '',
      support4k: window.support4k || false,
      xSupportedResolution: window.xSupportedResolution || '',
      xSupportedAudio: window.xSupportedAudio || [],
      dolbyAtmos: window.dolbyAtmos || false,
      duid: window.duid || '',
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Get current device capabilities
   */
  getDeviceCapabilities(): DeviceCapabilities {
    return this.deviceCapabilities;
  }

  /**
   * Get device capabilities as a simple object for easy access
   */
  getDeviceInfo(): {
    deviceModel: string;
    support4k: boolean;
    resolution: string;
    dolbyAtmos: boolean;
  } {
    return {
      deviceModel: this.deviceCapabilities.deviceModel,
      support4k: this.deviceCapabilities.support4k,
      resolution: this.deviceCapabilities.xSupportedResolution,
      dolbyAtmos: this.deviceCapabilities.dolbyAtmos,
    };
  }

  /**
   * Force re-detect device capabilities
   */
  refreshDeviceCapabilities(): void {
    this.initDeviceCapabilities();
  }

  getContentDetailInfo(
    resource: ResourceVm,
    isHeroFocused = false
  ): ContentDetailInfo {
    return {
      contentTags: this.getContentTags(resource),
      advisories: this.getAdvisories(resource),
      customTags: this.getCustomTags(resource, isHeroFocused),
      is4k: this.checkIs4k(resource),
      isDolbyAudio: this.checkIsDolbyAudio(resource),
      isAtmos: this.checkIsAtmos(resource),
      rating: this.getRatings(resource),
      heroTags: this.heroTags(resource),
      audioQualityTag: this.getAudioQualityTag(resource),
      multiLingualAudio: this.getMultiLingualAudio(resource),
      is51: this.checkIs51(resource),
    };
  }

  refreshCustomTags(
    resource: ResourceVm,
    isHeroFocused = false
  ): Tags[] | null {
    return this.getCustomTags(resource, isHeroFocused);
  }

  private getMultiLingualAudio(resource: ResourceVm) {
    if (resource.multiLingualAudio?.length > 1) {
      return 'Watch in Telugu & Tamil';
    }
    return null;
  }

  private isTagIncluded(tagType: AppConfigKeys): boolean {
    const { subscriptionUrn } = this.userEntlmnt;
    return !!(
      subscriptionUrn &&
      this.configService.applicationConfig[tagType]
        ?.toLowerCase()
        ?.includes(subscriptionUrn?.toLowerCase())
    );
  }

  private checkIs4k(resource: ResourceVm): boolean {
    const isUserEligibleToDisplayContentQuality =
      this.isUserEligibleToDisplayContentQuality() || false;

    const entitlementSupports4k =
      this.isTagIncluded(AppConfigKeys.VIDEO_4K_TAG) ||
      isUserEligibleToDisplayContentQuality;
    const is4kSupported =
      entitlementSupports4k && !!resource?.is4k && !!window.support4k;
    return is4kSupported;
  }

  private getAudioQualityTag(resource: ResourceVm): Tags {
    const isUserEligibleToDisplayContentQuality =
      this.isUserEligibleToDisplayContentQuality() || false;
    const audioQuality = resource.audioQuality;
    const dolbyAtmosSupported = this.authService.isAtmos();
    const dolbyAudioSupported = this.authService.isDolby();

    const hasEntitlementFor = (key: AppConfigKeys) =>
      this.isTagIncluded(key) || isUserEligibleToDisplayContentQuality;

    const entitledForAtmos = hasEntitlementFor(
      AppConfigKeys.AUDIO_DOLBYATMOS_TAG
    );
    const entitledForDolby51 = hasEntitlementFor(
      AppConfigKeys.AUDIO_DOLBY51_TAG
    );
    const entitledFor51 = hasEntitlementFor(AppConfigKeys.AUDIO_51_TAG);

    if (
      dolbyAtmosQualities
        .map((it) => it.toLowerCase())
        .includes(audioQuality?.toLowerCase())
    ) {
      if (dolbyAtmosSupported && entitledForAtmos) {
        return {
          key: AUDIO_DOLBY_ATMOS,
        };
      } else if (
        dolbyAudioSupported &&
        (entitledForAtmos || entitledForDolby51)
      ) {
        return {
          key: AUDIO_DOLBY_AUDIO,
        };
      } else if (dolbyAudioSupported && entitledFor51) {
        return {
          value: AUDIO_51,
        };
      }
    } else if (
      dolbyAudioQualities
        .map((it) => it.toLowerCase())
        .includes(audioQuality?.toLowerCase())
    ) {
      if (dolbyAudioSupported && (entitledForAtmos || entitledForDolby51)) {
        return {
          key: AUDIO_DOLBY_AUDIO,
        };
      }
    }
    return null;
  }

  private checkIs51(resource: ResourceVm): boolean {
    return this.getAudioQualityTag(resource)?.value === AUDIO_51;
  }

  private checkIsDolbyAudio(resource: ResourceVm): boolean {
    return this.getAudioQualityTag(resource)?.key === AUDIO_DOLBY_AUDIO;
  }

  private checkIsAtmos(resource: ResourceVm): boolean {
    return this.getAudioQualityTag(resource)?.key === AUDIO_DOLBY_ATMOS;
  }

  private getAdvisories(resource: ResourceVm): string {
    return this.getAdvisoriesList(resource).join(' • ');
  }

  getAdvisoriesList(resource: ResourceVm): string[] {
    const lang = this.languageService.currLang;
    const result: string[] = [];
    if (
      resource.contentGenre &&
      Object.keys(resource.contentGenre)?.length > 0
    ) {
      result.push(...resource.contentGenre[lang]);
    }
    if (resource.advisories?.length) {
      result.push(
        ...resource.advisories.map((advisory) =>
          this.languageService.getTranslatedText(`content_detail.${advisory}`)
        )
      );
    }
    return result;
  }

  private getContentTags(resource: ResourceVm): string {
    const result: string[] = [];
    const { releaseYear, runningTime, runningTimeinMins, totalNoOfSeasons } =
      resource;

    if (releaseYear) {
      result.push(releaseYear.toString());
    }
    if (runningTime) {
      let timeToPush = '';
      if (!runningTimeinMins) {
        if (resource.offset > 0) {
          timeToPush = `${resource.offset}s`;
        }
      } else {
        // Remove " 0s" or " 0m" parts and handle "0m"/"0s" cases
        timeToPush = runningTime
          .replace(/\s?0m\b/g, '')
          .replace(/\s?0s\b/g, '')
          .trim();
        if (timeToPush === '0m' || timeToPush === '0s') {
          timeToPush = '';
        }
      }

      if (timeToPush) {
        result.push(timeToPush);
      }
    }
    if (totalNoOfSeasons) {
      result.push(
        `${totalNoOfSeasons} ${this.languageService.getTranslatedText(
          totalNoOfSeasons > 1 ? 'player.seasons' : 'player.season'
        )}`
      );
    }
    return result.join(' • ');
  }

  private getRatings(resource: ResourceVm): string {
    return resource.ratings?.length && resource.ratings[0].v?.toUpperCase();
  }

  private getCustomTags(
    resource: ResourceVm,
    isHeroFocused = false
  ): Tags[] | null {
    const lang = this.languageService.currLang;
    const tagsSource = isHeroFocused ? resource.heroTags : resource.customTags;

    if (!tagsSource || Object.keys(tagsSource).length === 0) return null;

    // Use the current language, fallback to 'en' if not present
    const tagList = tagsSource[lang] || tagsSource['en'];
    const tagListEn = tagsSource['en'];
    if (!tagList) return null;

    // Map icon key based on English value, but show translated value
    return tagList.map((it: string, idx: number) => {
      const enValue = tagListEn && tagListEn[idx] ? tagListEn[idx] : it;
      return {
        key: ['Premium', 'Rent'].includes(enValue) ? enValue : null,
        value: it,
      };
    });
  }

  private isUserEligibleToDisplayContentQuality(): Boolean {
    const userLoggedIn = this.authService.isLoggedIn();
    const subscription = this.userEntlmnt.subscription;
    const account =
      this.accountProfileService.account?.GetAccountProfileResponseMessage;
    const isSubscribed =
      Boolean(account?.subscriptionStatus) && Boolean(subscription);
    const isChurnedUser = subscription?.status == EntitlementStatus.FINAL_BILL;
    return !userLoggedIn || isChurnedUser || !isSubscribed;
  }

  private heroTags(resource: ResourceVm): string {
    const result: string[] = [];
    const { releaseYear, runningTime, totalNoOfSeasons } = resource;

    if (releaseYear) {
      result.push(releaseYear.toString());
    }
    if (runningTime) {
      const timeToPush = runningTime
        .replace(/\s?0m\b/g, '')
        .replace(/\s?0s\b/g, '')
        .trim();
      if (timeToPush && timeToPush !== '0m' && timeToPush !== '0s') {
        result.push(timeToPush);
      }
    }
    if (totalNoOfSeasons) {
      result.push(
        `${totalNoOfSeasons} ${this.languageService.getTranslatedText(
          'player.seasons'
        )}`
      );
    }
    result.push(this.getAdvisories(resource));
    return result.join(' • ');
  }

  getComputedImageWidth(imageUrl: string, heightInVh: number = 18.426) {
    // Extract aspect ratio from URL (7x2 in this case)
    const aspectRatioMatch = imageUrl.match(/\d+x\d+/);
    if (!aspectRatioMatch) return null;

    const [width, height] = aspectRatioMatch[0].split('x').map(Number);
    const aspectRatio = width / height;

    // Calculate width in vw based on height and aspect ratio
    const widthInVw = heightInVh * aspectRatio;

    return widthInVw + 'vh';
  }

  getOptimizedImageUrl(imageUrl: string): string {
    if (!imageUrl) return '';

    const updatedUrl = this.updateImageUrl(imageUrl);
    return updatedUrl.replace('jpg', 'png');
  }

  updateImageUrl(url: string, heightValue: string = '18.426'): string {
    try {
      const windowHeight = window.innerHeight;
      const px = Math.round((Number(heightValue) / 100) * windowHeight);
      const heightPx = px.toString();
      const parsedUrl = new URL(url);

      // Remove width parameter to allow server to provide optimal width
      parsedUrl.searchParams.delete('width');

      // Set dynamic height based on viewport
      parsedUrl.searchParams.set('height', heightPx);

      return parsedUrl.toString();
    } catch (error) {
      // Fallback to original URL if parsing fails
      return url;
    }
  }
}
